/**
 * <ChecklistView /> — new-hire's mobile-first onboarding checklist.
 *
 * Route: /onboarding
 *
 * Reads the current user's checklist_items, displays a progress bar and
 * one card per item. Manager-driven items are grouped in a separate section
 * below the employee's own tasks so the list doesn't feel longer than the
 * work actually required of them. When 100%, shows a celebration screen.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, FileSignature, Upload as UploadIcon, CheckCircle2, PartyPopper, ArrowRight, RefreshCw } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { useApp } from '../../../context/AppContext';
import {
  subscribeChecklistItems,
  subscribeProgress,
} from '../services/onboardingService';
import '../Onboarding.css';

function StatusBadge({ status, driver }) {
  if (driver === 'manager' && status !== 'completed') {
    return <span className="onboarding-badge locked">Manager will complete</span>;
  }
  if (status === 'completed') return <span className="onboarding-badge completed">Done</span>;
  if (status === 'in_progress') return <span className="onboarding-badge in-progress">In progress</span>;
  return <span className="onboarding-badge pending">To do</span>;
}

function ItemCard({ item, onAction }) {
  const locked = item.driver === 'manager' && item.status !== 'completed';
  const completed = item.status === 'completed';
  return (
    <div className={`onboarding-card ${locked ? 'locked' : ''} ${completed ? 'completed' : ''}`}>
      <div style={{ flex: 1 }}>
        <div className="onboarding-card-title">
          {item.type === 'esign' && <FileSignature size={14} style={{ marginRight: 6 }} aria-hidden="true" />}
          {item.type === 'upload' && <UploadIcon size={14} style={{ marginRight: 6 }} aria-hidden="true" />}
          {item.type === 'manager_verify' && <Lock size={14} style={{ marginRight: 6 }} aria-hidden="true" />}
          {completed && <CheckCircle2 size={14} color="#3b6e4a" style={{ marginRight: 6 }} aria-hidden="true" />}
          {item.title}
        </div>
        <div className="onboarding-card-meta">
          <StatusBadge status={item.status} driver={item.driver} />
        </div>
      </div>
      <div className="onboarding-card-action">
        {locked ? null : item.type === 'esign' ? (
          <button
            className="onboarding-btn"
            onClick={() => onAction(`/onboarding/sign/${item.id}`)}
            aria-label={`${completed ? 'View' : 'Start'} ${item.title}`}
          >
            {completed ? 'View' : 'Start'}
          </button>
        ) : item.type === 'upload' ? (
          <button
            className="onboarding-btn"
            onClick={() => onAction(`/onboarding/upload/${item.id}`)}
            aria-label={`${completed ? 'View' : 'Upload'} ${item.title}`}
          >
            {completed ? 'View' : 'Upload'}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default function ChecklistView() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { state } = useApp();

  const employee = useMemo(
    () =>
      user?.email
        ? state.employees.find((e) => e.email?.toLowerCase() === user.email.toLowerCase())
        : null,
    [user, state.employees]
  );

  const [items, setItems] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadTimeout, setLoadTimeout] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    if (!employee?.id) return undefined;
    setLoadTimeout(false);
    const unsubItems = subscribeChecklistItems(employee.id, (rows) => {
      setItems(rows);
      setLoading(false);
    });
    const unsubProg = subscribeProgress(employee.id, setProgress);
    const timeoutId = setTimeout(() => setLoadTimeout(true), 8000);
    return () => { clearTimeout(timeoutId); unsubItems(); unsubProg(); };
  }, [employee?.id, retryNonce]);

  const totals = useMemo(() => {
    const total = items.length;
    const completed = items.filter((i) => i.status === 'completed').length;
    const percent = total ? Math.round((completed / total) * 100) : 0;
    return { total, completed, percent };
  }, [items]);

  const { employeeItems, managerItems } = useMemo(() => {
    const employeeItems = items.filter((i) => i.driver !== 'manager');
    const managerItems = items.filter((i) => i.driver === 'manager');
    return { employeeItems, managerItems };
  }, [items]);

  const nextItem = useMemo(
    () => employeeItems.find((i) => i.status !== 'completed'),
    [employeeItems]
  );

  if (!employee) {
    return (
      <div className="onboarding-shell">
        <div className="onboarding-empty">
          <h2>No employee record</h2>
          <p>We could not find an employee record for your account. Please contact your manager.</p>
        </div>
      </div>
    );
  }

  if (loading && loadTimeout) {
    return (
      <div className="onboarding-shell">
        <div className="onboarding-error" role="alert">
          <p style={{ margin: '0 0 12px' }}>
            Your checklist is taking longer than expected to load. Check your connection and try again.
          </p>
          <button
            className="onboarding-btn"
            onClick={() => { setLoading(true); setRetryNonce((n) => n + 1); }}
          >
            <RefreshCw size={14} style={{ marginRight: 6 }} aria-hidden="true" /> Try again
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="onboarding-shell"><div className="loading-screen"><div className="spinner-large" /><p>Loading…</p></div></div>;
  }

  if (items.length === 0) {
    const managerEmail = employee.manager_email || 'hello@twosisterscleaning.com';
    return (
      <div className="onboarding-shell">
        <div className="onboarding-empty">
          <h2>Almost ready</h2>
          <p>No onboarding items have been assigned to you yet. Your manager will provision them shortly.</p>
          <p>
            <a className="onboarding-btn secondary" href={`mailto:${managerEmail}?subject=Onboarding%20checklist%20not%20assigned%20yet`}>
              Contact my manager
            </a>
          </p>
        </div>
      </div>
    );
  }

  const allDone = totals.total > 0 && totals.completed === totals.total;
  const percentValue = progress?.percent_complete ?? totals.percent;

  return (
    <div className="onboarding-shell">
      <div className="onboarding-header">
        <h1>Welcome, {employee.firstName}!</h1>
        <p style={{ color: '#555', margin: 0 }} aria-live="polite">
          Let's finish setting up your file. {totals.completed} of {totals.total} done.
        </p>
      </div>

      <div
        className="onboarding-progress-bar"
        role="progressbar"
        aria-valuenow={percentValue}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Onboarding progress: ${percentValue}% complete`}
      >
        <div style={{ width: `${percentValue}%` }} />
      </div>

      {nextItem && !allDone && (
        <button
          className="onboarding-next-cta"
          onClick={() => navigate(
            nextItem.type === 'esign'
              ? `/onboarding/sign/${nextItem.id}`
              : `/onboarding/upload/${nextItem.id}`
          )}
        >
          <span>
            <span className="onboarding-next-cta-label">Next up</span>
            <span className="onboarding-next-cta-title">{nextItem.title}</span>
          </span>
          <ArrowRight size={18} aria-hidden="true" />
        </button>
      )}

      {allDone ? (
        <div className="onboarding-celebration">
          <PartyPopper size={56} color="#c9a227" aria-hidden="true" />
          <h2>All done!</h2>
          <p>Nice work. You'll be added to the schedule shortly.</p>
        </div>
      ) : null}

      {employeeItems.length > 0 && (
        <section aria-label="Your tasks">
          {employeeItems.map((item) => (
            <ItemCard key={item.id} item={item} onAction={navigate} />
          ))}
        </section>
      )}

      {managerItems.length > 0 && (
        <section aria-label="Handled by your manager">
          <h2 className="onboarding-section-heading">Handled by your manager</h2>
          {managerItems.map((item) => (
            <ItemCard key={item.id} item={item} onAction={navigate} />
          ))}
        </section>
      )}
    </div>
  );
}
